import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CsvSubmitResponse, QueryResultWrapper } from '../../../models/chat.model';
import { PlanLimitError, PlanLimitSource } from '../models/enforcement.model';

const PLAN_LIMIT_TEXT = 'the account has reached the configured plan limit';
const PLAN_LIMIT_FALLBACK_MESSAGE = 'The account has reached the configured plan limit.';
const UPLOAD_FORBIDDEN_STATUS = 'forbidden';
const PLAN_LIMIT_MESSAGE_PATHS = [
  ['details', 'error'],
  ['details', 'message'],
  ['error'],
  ['message'],
  ['detail'],
  ['response']
] as const;

@Injectable({ providedIn: 'root' })
export class PlanLimitErrorService {
  isPlanLimitText(message: unknown): boolean {
    if (typeof message !== 'string' || !message.trim()) {
      return false;
    }
    return message.trim().toLowerCase().includes(PLAN_LIMIT_TEXT);
  }

  fromHttpError(error: unknown, source: PlanLimitSource): PlanLimitError | null {
    if (!error || typeof error !== 'object') {
      return null;
    }

    const httpError = error as Partial<HttpErrorResponse>;
    const body: unknown = httpError.error;
    const status = typeof httpError.status === 'number' ? httpError.status : undefined;
    const planLimitMessage = this.extractPlanLimitMessage(body);

    if (planLimitMessage) {
      return this.buildError(source, body, status, planLimitMessage);
    }

    if (
      source === 'upload' &&
      status === 403 &&
      this.bodyIndicatesForbiddenStatus(body) &&
      !this.extractVisibleMessage(body)
    ) {
      return this.buildError(source, body, status, PLAN_LIMIT_FALLBACK_MESSAGE);
    }

    return null;
  }

  fromSuccessBody(body: unknown, source: PlanLimitSource): PlanLimitError | null {
    const planLimitMessage = this.extractPlanLimitMessage(body);
    if (!planLimitMessage) {
      return null;
    }
    return this.buildError(source, body, undefined, planLimitMessage);
  }

  fromChatWrapper(wrapper: QueryResultWrapper | null | undefined): PlanLimitError | null {
    return this.fromSuccessBody(wrapper, 'chat');
  }

  fromCsvResponse(response: CsvSubmitResponse | null | undefined): PlanLimitError | null {
    return this.fromSuccessBody(response, 'csv');
  }

  private buildError(
    source: PlanLimitSource,
    body: unknown,
    httpStatus: number | undefined,
    fallbackMessage: string
  ): PlanLimitError {
    const backendMessage = this.extractMessage(body) ?? fallbackMessage;
    const limitCode = this.extractLimitCode(body);
    const consumedQuantity = this.extractNumber(body, 'consumedQuantity');
    const limitValue = this.extractNumber(body, 'limitValue');

    const error: PlanLimitError = {
      source,
      backendMessage
    };
    if (typeof httpStatus === 'number') {
      error.httpStatus = httpStatus;
      error.statusCode = httpStatus;
    }
    if (limitCode) {
      error.limitCode = limitCode;
    }
    if (typeof consumedQuantity === 'number') {
      error.consumedQuantity = consumedQuantity;
    }
    if (typeof limitValue === 'number') {
      error.limitValue = limitValue;
    }
    return error;
  }

  private bodyIndicatesForbiddenStatus(body: unknown): boolean {
    const normalizedBody = this.normalizeBody(body);
    if (!normalizedBody || typeof normalizedBody !== 'object') {
      return false;
    }
    const status = (normalizedBody as { status?: unknown }).status;
    return typeof status === 'string' && status.trim().toLowerCase() === UPLOAD_FORBIDDEN_STATUS;
  }

  private extractMessage(body: unknown): string | null {
    return this.extractPlanLimitMessage(body) ?? this.extractVisibleMessage(body);
  }

  private extractPlanLimitMessage(body: unknown): string | null {
    const normalizedBody = this.normalizeBody(body);
    if (typeof normalizedBody === 'string') {
      return this.isPlanLimitText(normalizedBody) ? normalizedBody : null;
    }
    if (!normalizedBody || typeof normalizedBody !== 'object') {
      return null;
    }

    for (const path of PLAN_LIMIT_MESSAGE_PATHS) {
      const value = this.readPath(normalizedBody, path);
      if (typeof value === 'string' && this.isPlanLimitText(value)) {
        return value;
      }
    }
    return null;
  }

  private extractVisibleMessage(body: unknown): string | null {
    const normalizedBody = this.normalizeBody(body);
    if (!normalizedBody) {
      return null;
    }
    if (typeof normalizedBody === 'string') {
      return normalizedBody.trim() ? normalizedBody : null;
    }
    if (typeof normalizedBody === 'object') {
      for (const path of PLAN_LIMIT_MESSAGE_PATHS) {
        const value = this.readPath(normalizedBody, path);
        if (typeof value === 'string' && value.trim()) {
          return value;
        }
      }
    }
    return null;
  }

  private normalizeBody(body: unknown): unknown {
    if (!body) {
      return null;
    }
    if (typeof body === 'string') {
      const trimmed = body.trim();
      if (!trimmed) {
        return null;
      }
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return body;
      }
      try {
        return JSON.parse(trimmed) as unknown;
      } catch {
        return body;
      }
    }
    return body;
  }

  private readPath(body: object, path: readonly string[]): unknown {
    let current: unknown = body;
    for (const segment of path) {
      if (!current || typeof current !== 'object') {
        return undefined;
      }
      current = (current as Record<string, unknown>)[segment];
    }
    return current;
  }

  private extractLimitCode(body: unknown): string | undefined {
    const normalizedBody = this.normalizeBody(body);
    if (!normalizedBody || typeof normalizedBody !== 'object') {
      return undefined;
    }
    const value = (normalizedBody as { limitCode?: unknown }).limitCode
      ?? this.readPath(normalizedBody, ['details', 'limitCode']);
    return typeof value === 'string' && value.trim() ? value : undefined;
  }

  private extractNumber(body: unknown, key: string): number | undefined {
    const normalizedBody = this.normalizeBody(body);
    if (!normalizedBody || typeof normalizedBody !== 'object') {
      return undefined;
    }
    const value = (normalizedBody as Record<string, unknown>)[key]
      ?? this.readPath(normalizedBody, ['details', key]);
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
  }
}
